'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import InsightsIcon from '@mui/icons-material/Insights';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import type { FeatureHighlightsProps } from './types';

/**
 * Feature highlights component displaying Boty's key benefits.
 *
 * Supports three variants:
 * - 'full': Icon + title + description (desktop)
 * - 'compact': Icon + title only
 * - 'icons-only': Just icons with tooltips (mobile)
 *
 * Epic 6 Note: This component is designed to be portable to the marketing site.
 * It accepts props for customization and uses i18n for all text.
 */
export function FeatureHighlights({ variant = 'full' }: FeatureHighlightsProps) {
  const t = useTranslations('creator.landing.features');

  const features = [
    {
      key: 'free',
      icon: <MoneyOffIcon />,
      title: t('free.title'),
      description: t('free.description'),
    },
    {
      key: 'speed',
      icon: <BoltIcon />,
      title: t('speed.title'),
      description: t('speed.description'),
    },
    {
      key: 'availability',
      icon: <SmartToyIcon />,
      title: t('availability.title'),
      description: t('availability.description'),
    },
    {
      key: 'insights',
      icon: <InsightsIcon />,
      title: t('insights.title'),
      description: t('insights.description'),
    },
    {
      key: 'leadCapture',
      icon: <ContactMailIcon />,
      title: t('leadCapture.title'),
      description: t('leadCapture.description'),
    },
  ];

  // Icons-only variant (mobile): Just icons with tooltips
  if (variant === 'icons-only') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          py: 1,
        }}
      >
        {features.map((feature) => (
          <Tooltip key={feature.key} title={feature.title} arrow>
            <Box
              sx={{
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem',
                },
              }}
            >
              {feature.icon}
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  }

  // Compact variant: Icon + title in a row
  if (variant === 'compact') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { sm: 'wrap' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          py: 0.5,
        }}
      >
        {features.map((feature) => (
          <Box
            key={feature.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <Box sx={{ color: 'primary.main', display: 'flex' }}>
              {feature.icon}
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {feature.title}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // Full variant (default): Icon + title + description in cards
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        py: 3,
      }}
    >
      {features.map((feature) => (
        <Box
          key={feature.key}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'center' },
            textAlign: { xs: 'start', md: 'center' },
            gap: 1,
          }}
        >
          <Box
            sx={{
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'primary.900' : 'primary.50',
              '& .MuiSvgIcon-root': {
                fontSize: '1.5rem',
              },
            }}
          >
            {feature.icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {feature.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {feature.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
